
import React from 'react';
import { User2, Store, ShoppingBag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RecentActivity } from '@/services/dashboardService';
import { formatDistanceToNow } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface RecentActivitiesProps {
  activities: RecentActivity[];
  isLoading: boolean;
}

const RecentActivities: React.FC<RecentActivitiesProps> = ({ activities, isLoading }) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-md font-medium">Recent Activities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((_, index) => (
              <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-md font-medium">Recent Activities</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {activities.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No recent activities found</p>
          ) : (
            activities.map((activity, index) => {
              const timeAgo = formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true });
              
              if (activity.type === 'user_registered') {
                return (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                    <div className="bg-blue-100 p-2 rounded-full">
                      <User2 className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">New user registered</p>
                      <p className="text-sm text-gray-500">{activity.data.email}</p>
                      <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
                    </div>
                  </div>
                );
              }
              
              if (activity.type === 'ahavexer_onboarded') {
                return (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                    <div className="bg-green-100 p-2 rounded-full">
                      <Store className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">New ahavexer onboarded</p>
                      <p className="text-sm text-gray-500">{activity.data.email}</p>
                      <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
                    </div>
                  </div>
                );
              }
              
              if (activity.type === 'order_completed') {
                return (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                    <div className="bg-orange-100 p-2 rounded-full">
                      <ShoppingBag className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium">Order completed</p>
                      <p className="text-sm text-gray-500">
                        Order #{activity.data.id?.substring(0, 8) || 'N/A'} - 
                        {activity.data.orderTotal ? formatCurrency(activity.data.orderTotal) : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
                    </div>
                  </div>
                );
              }
              
              return null;
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentActivities;
